import React, { useEffect, useRef, memo, useState } from 'react';
import config from '../../config';
import ReactQuill from 'react-quill';
import Quill from 'quill';
import 'react-quill/dist/quill.snow.css';
import '../../styles/content.scss';
import BlotFormatter from '@enzedonline/quill-blot-formatter2';
import QuillFileUploader from './QuillFileUploader';
import QuillButtonBlot from './QuillButtonBlot';
import QuillButtonHandler from './QuillButtonHandler';
import './ContentEditor.css';

import RichTextEditor from 'quill-react-commercial';
import 'quill-react-commercial/lib/index.css';

// Register the custom button blot
Quill.register('blots/button', QuillButtonBlot);
Quill.register('modules/blotFormatter', BlotFormatter);

// Ensure proper list format registration
const List = Quill.import('formats/list');
//const ListItem = Quill.import('formats/list/item');
Quill.register(List, true);
//Quill.register(ListItem, true);

const ContentEditor = memo(({ value, onChange }) => {
  const fileUploaderRef = useRef(null);
  const buttonHandlerRef = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  const [internalValue, setInternalValue] = useState(value || '');

  const [initialValue, setInitialValue] = useState(value || '');
  const isInternalChange = useRef(false);
  
  // Update internal value when prop changes
  useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false;
    } else {
      setInitialValue(value);
    }
  }, [value]);
  
  // Stable modules configuration
  const modules = React.useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered' }],  //, { 'list': 'bullet' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        [{ 'align': [] }],
        ['link', 'image', 'file', 'button'],
        ['clean']
      ],
      handlers: {
        'image': function() {
          if (fileUploaderRef.current) {
            fileUploaderRef.current.selectLocalImage();
          }
        },
        'file': function() {
          if (fileUploaderRef.current) {
            fileUploaderRef.current.selectFile();
          }
        },
        'button': function() {
          if (buttonHandlerRef.current) {
            buttonHandlerRef.current.insertButton();
          }
        }
      }
    },
    blotFormatter: {
      align: {
        allowAligning: true,
        alignments: ['left', 'center', 'right']
      },
      resize: {
        allowResizing: true
      }
    },
    clipboard: {
      matchVisual: false,
    }
  }), []);
  
  const formats = React.useMemo(() => [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list',
    //'bullet',
    'indent',
    'align',
    'link', 'image', 'button'
  ], []);
  
  useEffect(() => {
    if (quillRef.current && !isInitialized) {
      const quill = quillRef.current;
      fileUploaderRef.current = new QuillFileUploader(quill, {});
      buttonHandlerRef.current = new QuillButtonHandler(quill);
      setIsInitialized(true);
    }
  }, [isInitialized, value]);
  
  // Handle value changes more carefully
  const handleChange = React.useCallback(() => {
    const content = quillRef.current.getSemanticHTML();
    isInternalChange.current=true;
    onChange(content);
  }, [onChange]);
  
  // Reset editor if there are persistent issues
  const resetEditor = React.useCallback(() => {
    setIsInitialized(false);
    setEditorKey(prev => prev + 1);
  }, []);
  
  // Handle editor errors
  useEffect(() => {
    const handleError = (error) => {
      console.warn('Quill editor error:', error);
      if (error.message && error.message.includes('addRange')) {
        // Reset editor on selection errors
        setTimeout(resetEditor, 100);
      }
    };
    
    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, [resetEditor]);
  
  const quillRef = useRef({});
  const getQuill = (quillIns) => {
    console.log(quillIns);
    quillRef.current = quillIns;
  };
  

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = error => reject(error);
    });
  }
  

  const imgUploadApi = async (formData) => {
    // Extract file from FormData
    const file = formData.get('file');
    
    // Convert to base64 (your current backend expects this)
    const fileContent = await fileToBase64(file);
    
    // Use your existing API
    const response = await fetch(`${config.api.url}upload`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('idToken')}`
      },
      body: JSON.stringify({
        fileContent: fileContent,
        fileName: file.name,
        fileType: file.type
      })
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status}`);
    }

    const result = await response.json();
    return result.fileUrl; // Return the URL string as required
  };

  return (
    <div className="rich-text-editor">
      {false && <ReactQuill
        key={editorKey}
        ref={quillRef}
        theme="snow"
        value={internalValue}
        onChange={handleChange}
        modules={modules}
        formats={formats}
        preserveWhitespace={true}
        bounds=".rich-text-editor"
      />}
      <RichTextEditor
        modules={{
          table: {},
          codeHighlight: false,
          toolbarOptions:[
            [{header: [1,2,3,false]}],
            ['bold', 'italic', 'underline'],
            // Color picker — applies inline `style="color: …"` to selected text.
            // For link text this overrides the default blue defined in CSS.
            [{color: []}],
            [
              {list:'ordered'},
              {list:'bullet'},
              {indent:'-1'},
              {indent:'+1'},
              {align:[]}
            ],
            ['link', 'image'],
            ['clean']
          ],
          imageHandler: {
            imgUploadApi: imgUploadApi
          }
        }}
        getQuill={getQuill}
        content={initialValue}
        onChange={handleChange}
        bounds=".review-editor"
      />
    </div>
  );
});

ContentEditor.displayName = 'ContentEditor';

export default ContentEditor;
